import React, { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { MdTranslate, MdMenu, MdClose } from "react-icons/md";
import { motion, AnimatePresence } from 'framer-motion';
import './Header.css';
import logoMain from '../assets/logo_main2.png';
import logoMainReverse from '../assets/logo_main2_reserve.webp';

function Header() {
  const { t, i18n } = useTranslation();
  const [showLanguageDropdown, setShowLanguageDropdown] = useState(false);
  const [isMenuOpen, setMenuOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const [isDarkMode, setIsDarkMode] = useState(true); // Default dark
  const languageSwitcherRef = useRef(null);
  const headerRef = useRef(null);

  useEffect(() => {
    // Check local storage or preference
    const storedTheme = localStorage.getItem('theme');
    if (storedTheme === 'light') {
      setIsDarkMode(false);
      document.body.classList.add('light-mode');
    } else {
      setIsDarkMode(true);
      document.body.classList.remove('light-mode');
    }
  }, []);

  const toggleTheme = () => {
    if (isDarkMode) {
      document.body.classList.add('light-mode');
      localStorage.setItem('theme', 'light');
    } else {
      document.body.classList.remove('light-mode');
      localStorage.setItem('theme', 'dark');
    }
    setIsDarkMode(!isDarkMode);
  };

  useEffect(() => {
    const handleScroll = () => {
      if (window.scrollY > 50) {
        setScrolled(true);
      } else {
        setScrolled(false);
      }
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    function handleClickOutside(event) {
      if (languageSwitcherRef.current && !languageSwitcherRef.current.contains(event.target)) {
        setShowLanguageDropdown(false);
      }
      if (headerRef.current && !headerRef.current.contains(event.target)) {
        // Don't close if clicking the toggle button itself, handled by toggleMenu
        if (!event.target.closest('.menu-toggle-button')) {
          // setMenuOpen(false); // Optional: decide if clicking outside closes menu
        }
      }
    }
    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [languageSwitcherRef, headerRef]);


  const changeLanguage = (lng) => {
    i18n.changeLanguage(lng);
    setShowLanguageDropdown(false);
  };

  const toggleLanguageDropdown = () => {
    setShowLanguageDropdown(prev => !prev);
  };

  const toggleMenu = () => {
    setMenuOpen(prev => !prev);
  };

  const closeMenu = () => {
    setMenuOpen(false);
  };

  const navVariants = {
    hidden: { opacity: 0, y: -20 },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        staggerChildren: 0.1,
        delayChildren: 0.2
      }
    }
  };

  const itemVariants = {
    hidden: { opacity: 0, y: -20 },
    visible: { opacity: 1, y: 0 }
  };

  return (
    <motion.header
      className={`header ${scrolled ? 'scrolled' : ''}`}
      ref={headerRef}
      initial={{ y: -100 }}
      animate={{ y: 0 }}
      transition={{ duration: 0.5 }}
    >
      <div className="header-container">
        <Link to="/" onClick={closeMenu}>
          <motion.img
            src={isDarkMode ? logoMainReverse : logoMain}
            alt="Vorpoint Logo"
            className="header-logo"
            whileHover={{ scale: 1.05 }}
            transition={{ duration: 0.2 }}
          />
        </Link>

        <div className="header-mobile-controls">
          <button className="menu-toggle-button" onClick={toggleMenu}>
            {isMenuOpen ? <MdClose /> : <MdMenu />}
          </button>
        </div>

        {/* Desktop Nav */}
        <nav className="header-nav desktop-nav">
          <ul>
            {['home', 'about', 'services', 'projects', 'contact', 'our_cost'].map((item) => (
              <motion.li key={item} whileHover={{ scale: 1.1 }} whileTap={{ scale: 0.95 }}>
                <Link to={item === 'home' ? '/' : `/${item === 'about' ? 'ueber-uns-neu' : item === 'services' ? 'leistungen' : item === 'projects' ? 'projekte' : item === 'contact' ? 'kontakt' : 'our_cost'}`} onClick={closeMenu}>
                  {t(`header.${item}`)}
                </Link>
              </motion.li>
            ))}
          </ul>
        </nav>

        {/* Mobile Nav Overlay */}
        <AnimatePresence>
          {isMenuOpen && (
            <motion.nav
              className="header-nav mobile-nav active"
              initial={{ opacity: 0, height: 0 }}
              animate={{ opacity: 1, height: 'auto' }}
              exit={{ opacity: 0, height: 0 }}
              transition={{ duration: 0.3 }}
            >
              <motion.ul
                variants={navVariants}
                initial="hidden"
                animate="visible"
              >
                {['home', 'about', 'services', 'projects', 'contact', 'our_cost'].map((item) => (
                  <motion.li key={item} variants={itemVariants}>
                    <Link to={item === 'home' ? '/' : `/${item === 'about' ? 'ueber-uns-neu' : item === 'services' ? 'leistungen' : item === 'projects' ? 'projekte' : item === 'contact' ? 'kontakt' : 'our_cost'}`} onClick={closeMenu}>
                      {t(`header.${item}`)}
                    </Link>
                  </motion.li>
                ))}
              </motion.ul>
            </motion.nav>
          )}
        </AnimatePresence>

        <div className="header-right">
          <a href="tel:+49-1778663796" className="header-cta-button" title={t('header.cta_title')}>
            <motion.div
              className="header-cta-content"
              whileHover={{ scale: 1.05 }}
            >
              <span
                className="header-cta-phone"
                aria-label="Phone number"
              >
                +49-1778663796
              </span>
            </motion.div>
          </a>

          <div className="language-switcher" ref={languageSwitcherRef}>
            <div style={{ display: 'flex', gap: '10px', alignItems: 'center' }}>
              <button
                onClick={toggleTheme}
                className="theme-toggle-button"
                aria-label="Toggle Theme"
                style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: '1.2rem', color: 'var(--color-text-secondary)' }}
              >
                {isDarkMode ? '🌙' : '☀️'}
              </button>
              <div style={{ position: 'relative' }}>
                <motion.button
                  className="language-toggle-button"
                  onClick={toggleLanguageDropdown}
                  whileHover={{ scale: 1.1 }}
                  whileTap={{ scale: 0.95 }}
                >
                  <MdTranslate style={{ marginRight: "0.5em" }} /> {i18n.language.toUpperCase()}
                </motion.button>
                {/* Dropdown content */}
                <AnimatePresence>
                  {showLanguageDropdown && (
                    <motion.div
                      className="language-dropdown"
                      initial={{ opacity: 0, y: -10 }}
                      animate={{ opacity: 1, y: 0 }}
                      exit={{ opacity: 0, y: -10 }}
                      transition={{ duration: 0.2 }}
                    >
                      <button onClick={() => changeLanguage('de')}>Deutsch</button>
                      <button onClick={() => changeLanguage('en')}>English</button>
                      <button onClick={() => changeLanguage('ar')}>العربية</button>
                    </motion.div>
                  )}
                </AnimatePresence>
              </div>
            </div>
          </div>
        </div>
      </div>
    </motion.header>
  );
}

export default Header;
